import {
  QueryClientContext,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback, useContext, useSyncExternalStore } from "react";
import type { MeResponse } from "@shared/types";
import type { PutUserPreferencesBody, UserPreferencesView } from "@shared/types/userPreferences";
import { apiRequest } from "@/lib/api";
import { browserTimeZone, canRenderZone, currentZoneName } from "@/lib/time/displayTime";
import { ME_QUERY_KEY } from "./use-me";

/**
 * The zone every instant on screen renders in (round 3, 2026-09-30): the zone the person chose
 * (`GET /me` `preferences.timezone`, one zone across every tenant and instance), else this
 * device's. Usage: `formatInstant(iso, useDisplayTz())` (`lib/time/displayTime.ts`).
 *
 * Owner decision (2026-09-30): nothing stores a zone the person did not pick. No stored zone
 * means "follow this device", and the device's zone is read on every render, so a person who
 * travels sees the local time where they are. (Round 3 stored the browser's zone once; that
 * froze the zone of the first device a person signed in on.)
 *
 * It reads `/me` from the query cache and never fetches it: the shell owns that request, and a
 * component rendered without a query client (or before `/me` arrives) still has a zone.
 */

export const USER_PREFERENCES_PATH = "/api/v2/me/preferences";

function readMe(client: QueryClient | undefined): MeResponse | undefined {
  return client?.getQueryData<MeResponse>(ME_QUERY_KEY);
}

function withZone(client: QueryClient, timezone: string | null): void {
  client.setQueryData<MeResponse>(ME_QUERY_KEY, (old) =>
    // The zone only: the rest of `preferences` (the last instance, round 4) stays as it was.
    old ? { ...old, preferences: { ...old.preferences, timezone } } : old,
  );
}

export interface DisplayTzChoice {
  /** The zone to render in, by its current IANA name. */
  zone: string;
  /** True when no zone is chosen (or the chosen one cannot render here): this device's zone. */
  followsDevice: boolean;
}

/** The choice from `/me` as the cache holds it now. */
function choiceOf(me: MeResponse | undefined): DisplayTzChoice {
  const stored = me?.preferences?.timezone ?? null;
  // A row stored before the alias table can hold an old name (Asia/Calcutta): read it as the
  // current one, so the picker selects the option it offers.
  if (canRenderZone(stored)) return { zone: currentZoneName(stored), followsDevice: false };
  return { zone: browserTimeZone(), followsDevice: true };
}

/**
 * The display zone read once, at the moment of an action (an export, #1078), without subscribing
 * the caller to `/me`: a page that only needs the zone when a button is pressed does not
 * re-render when `/me` arrives.
 */
export function displayTzNow(client: QueryClient | undefined): string {
  return choiceOf(readMe(client)).zone;
}

/** The display zone and where it comes from: the person's choice, or this device. */
export function useDisplayTzChoice(): DisplayTzChoice {
  const client = useContext(QueryClientContext);
  const subscribe = useCallback(
    (onChange: () => void) => (client ? client.getQueryCache().subscribe(onChange) : () => {}),
    [client],
  );
  const me = useSyncExternalStore(
    subscribe,
    () => readMe(client),
    () => readMe(client),
  );
  return choiceOf(me);
}

export function useDisplayTz(): string {
  return useDisplayTzChoice().zone;
}

/**
 * The person's choice from the user menu, or null to follow this device again; the new zone
 * renders everywhere at once.
 */
export function useSaveDisplayTz() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutUserPreferencesBody) =>
      apiRequest<UserPreferencesView>(USER_PREFERENCES_PATH, { method: "PUT", body }),
    onSuccess: (view) => withZone(client, view.timezone),
  });
}
